import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "./cityPricing";

const cagnesSurMer: ChauffeurPage = {
  slug: "cagnes-sur-mer",
  title: "Chauffeur privé à Cagnes-sur-Mer – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Cagnes-sur-Mer : transferts aéroport de Nice, hippodrome, Haut-de-Cagnes et plages, 7j/7.",
  keywords: [
    "chauffeur privé cagnes-sur-mer",
    "vtc cagnes-sur-mer",
    "chauffeur cagnes-sur-mer",
    "vtc hippodrome cagnes",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Cagnes-sur-Mer",
    subtitle:
      "Transferts aéroport de Nice, hippodrome de la Côte d’Azur, Haut-de-Cagnes et plages du Cros — 24h/24, 7j/7.",
    keywords: [
      "VTC Cagnes-sur-Mer",
      "chauffeur hippodrome Cagnes",
      "transfert aéroport Nice",
      "chauffeur Haut-de-Cagnes",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Cagnes-sur-Mer et au Haut-de-Cagnes",
    badges: [
      "Haut-de-Cagnes & hippodrome",
      "Suivi de vol",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Cagnes-sur-Mer",
    items: [
      {
        title: "Haut-de-Cagnes perché",
        description:
          "Village médiéval et Château-Musée Grimaldi : dépose au pied des ruelles pavées, là où la voiture ne monte plus.",
      },
      {
        title: "Hippodrome de la Côte d’Azur",
        description:
          "Réunions et grandes courses : dépose et reprise coordonnées, aller-retour de groupe sans souci de parking.",
      },
      {
        title: "Aéroport de Nice en ~12 min",
        description:
          "Liaison directe par l’A8 ou le bord de mer, avec suivi de vol et accueil personnalisé.",
      },
      {
        title: "Ancrage local",
        description:
          "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google, chauffeurs bilingues FR/EN.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Cagnes-sur-Mer",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Cagnes-sur-Mer ⇄ aéroport de Nice Côte d’Azur en ~12 min, avec suivi de vol et accueil aux terminaux.",
      },
      {
        title: "Hippodrome de la Côte d’Azur",
        text: "Journées de courses, réunions nocturnes et groupes : dépose au plus près des tribunes et retour coordonné.",
      },
      {
        title: "Haut-de-Cagnes & Musée Renoir",
        text: "Dépose au pied du village médiéval et liaison vers le musée Renoir des Collettes et son oliveraie.",
      },
      {
        title: "Cros-de-Cagnes & plages",
        text: "Village de pêcheurs, restaurants de bord de mer et plages, avec dépose au plus près.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, un chauffeur dédié pour vos déplacements sur la Côte d’Azur.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      {
        name: "Haut-de-Cagnes",
        description: "Village médiéval perché et Château-Musée Grimaldi.",
      },
      {
        name: "Hippodrome de la Côte d’Azur",
        description: "Champ de courses, réunions d’hiver et d’été.",
      },
      {
        name: "Musée Renoir (Les Collettes)",
        description: "Maison-atelier du peintre et oliveraie.",
      },
      {
        name: "Cros-de-Cagnes",
        description: "Village de pêcheurs, port et plages.",
      },
      {
        name: "Polygone Riviera",
        description: "Centre commercial à ciel ouvert, près de la sortie A8.",
      },
      {
        name: "Aéroport de Nice",
        description: "~12 min par l’A8 ou le bord de mer.",
      },
    ],
  },
  generic: {
    heading: "Cagnes-sur-Mer, entre Haut-de-Cagnes, hippodrome et bord de mer",
    subheading:
      "Du village médiéval au Cros-de-Cagnes, un chauffeur qui relie les trois visages de la ville.",
    paragraphs: [
      "Cagnes-sur-Mer se lit en trois strates : le Haut-de-Cagnes, village médiéval perché autour du Château-Musée Grimaldi, aux ruelles pavées où la voiture cède vite la place ; la plaine et son hippodrome de la Côte d’Azur ; et le Cros-de-Cagnes, ancien village de pêcheurs devenu front de mer animé.",
      "Pour le Haut-de-Cagnes, le chauffeur vous dépose au pied du village et vous reprend au même endroit, sans que vous cherchiez à vous garer dans les lacets. Le musée Renoir des Collettes, avec sa maison-atelier et son oliveraie, se rejoint dans la foulée.",
      "Les jours de courses à l’hippodrome, nous coordonnons dépose et reprise au plus près des tribunes, y compris pour les groupes et les réunions nocturnes. À ~12 min de l’aéroport de Nice, Cagnes est aussi une base idéale pour un transfert rapide ou une journée plage au Cros-de-Cagnes.",
    ],
    bullets: [
      "Dépose au pied du Haut-de-Cagnes",
      "Jours de courses coordonnés",
      "Aéroport de Nice à ~12 min",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Cagnes-sur-Mer",
    steps: [
      "Indiquez votre trajet ou vos adresses (aéroport, hippodrome, Haut-de-Cagnes, plages).",
      "Nous confirmons le véhicule adapté et le forfait ou la mise à disposition.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure.",
      "Dépose au pied du village, aux tribunes ou sur le front de mer, reprise sur demande.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Cagnes-sur-Mer"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Cagnes-sur-Mer ?",
      a: "Comptez environ 12 minutes par l’A8 ou le bord de mer hors heures de pointe. Nous suivons votre vol pour ajuster l’heure de prise en charge, dans les deux sens.",
    },
    {
      q: "Assurez-vous les trajets les jours de courses à l’hippodrome ?",
      a: "Oui. Les jours de réunion à l’hippodrome de la Côte d’Azur, nous déposons au plus près des tribunes et coordonnons la reprise, y compris pour les groupes et les courses nocturnes.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et les jours de courses ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous à Cagnes-sur-Mer ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Peut-on monter au Haut-de-Cagnes en voiture ?",
      a: "Les ruelles pavées du village médiéval sont étroites et en grande partie piétonnes. Le chauffeur vous dépose au pied du village, près du Château-Musée Grimaldi, puis vous reprend au même endroit.",
    },
  ],
  relatedLinks: [
    { label: "Voiture avec chauffeur à Nice", href: "/chauffeur/nice" },
    { label: "Accueil à l’aéroport de Nice", href: "/chauffeur/aeroport" },
    {
      label: "Chauffeur Côte d’Azur, tarifs et trajets",
      href: "/chauffeur/cote-d-azur",
    },
    { label: "Service de chauffeur à Cannes", href: "/chauffeur/cannes" },
  ],
  cta: {
    title: "Votre chauffeur privé à Cagnes-sur-Mer",
    text: "Hippodrome, Haut-de-Cagnes ou transfert aéroport — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Cagnes-sur-Mer 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Cagnes-sur-Mer : transfert aéroport de Nice (~12 min), hippodrome, Haut-de-Cagnes et plages du Cros. Bilingues, 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/cagnes-sur-mer",
  },
};

export default cagnesSurMer;
